import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import { getUserTenantId } from "@talentech/bff-login";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonColors,
  Grid,
  PageTitle,
  Row,
  Spinner,
  TalentechColors,
} from "@talentech/components";
import { ROUTES } from "@/App";
import ConfirmationModal from "@/components/ConfirmationModal/ConfirmationModal";
import { useGetPagesQuery } from "@/redux/reducer/pages.api";
import { useGetCustomerConfigQuery } from "@/redux/reducer/customers.api";
import {
  useCreateJobMutation,
  useGetJobQuery,
  usePublishJobMutation,
  useUnpublishJobMutation,
  useUpdateJobMutation,
} from "@/redux/reducer/directJobs.api";
import {
  DirectJob,
  DirectJobForm,
  JobStatus,
} from "@/redux/reducer/directJobs.types";
import JobFormFields from "./JobFormFields";
import JobPreviewModal from "./JobPreviewModal";
import {
  JobAdTemplateOption,
  jobAdTemplateOptions,
} from "./jobAdTemplateOptions";
import {
  EMPTY_JOB_FORM_VALUES,
  JobFormValues,
  toDirectJobForm,
  toJobFormValues,
} from "./jobForm";
import { NEW_JOB_ID, jobPath } from "./jobRoutes";
import JobStatusBadge from "./JobStatusBadge";
import {
  fieldErrorsOf,
  serverFieldErrors,
  validateJobForm,
} from "./jobFormValidation";

const FIX_FIELDS_MESSAGE = "Fix the highlighted fields";

interface JobFormActionsProps {
  job?: DirectJob;
  isDirty: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onPreview: () => void;
  onSave: (publish: boolean) => void;
  onUnpublish: () => void;
}

const JobFormActions: React.FC<JobFormActionsProps> = ({
  job,
  isDirty,
  isSaving,
  onCancel,
  onPreview,
  onSave,
  onUnpublish,
}) => {
  const isPublished = job?.status === JobStatus.PUBLISHED;
  return (
    <Box
      paper
      rounded
      spacing={3}
      className="position-sticky bottom-0 d-flex flex-wrap align-items-center gap-2 shadow"
      style={{ zIndex: 5 }}
    >
      <Box flex className="gap-2 align-items-center">
        {isDirty && <Badge color={TalentechColors.WARNING} label="Not saved" />}
        {job && <JobStatusBadge status={job.status} />}
      </Box>
      <Box flex className="flex-wrap justify-content-end gap-2 ms-auto">
        <Button
          label="Cancel"
          outline
          color={ButtonColors.PRIMARY}
          onClick={onCancel}
        />
        <Button
          label="Preview"
          outline
          color={ButtonColors.PRIMARY}
          icon={<i className="fa-sharp fa-light fa-eye me-2" />}
          onClick={onPreview}
        />
        {isPublished ? (
          <>
            <Button
              label="Update"
              color={ButtonColors.PRIMARY}
              isLoading={isSaving}
              disabled={isSaving}
              onClick={() => onSave(false)}
            />
            <Button
              label="Unpublish"
              outline
              color={ButtonColors.DANGER}
              disabled={isSaving}
              onClick={onUnpublish}
            />
          </>
        ) : (
          <>
            <Button
              label="Save draft"
              outline
              color={ButtonColors.PRIMARY}
              disabled={isSaving}
              onClick={() => onSave(false)}
            />
            <Button
              label="Publish"
              color={ButtonColors.PRIMARY}
              isLoading={isSaving}
              disabled={isSaving}
              onClick={() => onSave(true)}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

interface JobFormProps {
  tenantId: string;
  initialJob?: DirectJob;
  templateOptions: JobAdTemplateOption[];
}

const JobForm: React.FC<JobFormProps> = ({
  tenantId,
  initialJob,
  templateOptions,
}) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(initialJob);
  const [previewForm, setPreviewForm] = useState<DirectJobForm | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [createJob] = useCreateJobMutation();
  const [updateJob] = useUpdateJobMutation();
  const [publishJob] = usePublishJobMutation();
  const [unpublishJob] = useUnpublishJobMutation();

  const isPublished = job?.status === JobStatus.PUBLISHED;

  const showErrors = (
    formik: FormikProps<JobFormValues>,
    error: unknown,
    fallback: string
  ) => {
    const fieldErrors = fieldErrorsOf(error);
    if (fieldErrors.length) {
      formik.setErrors(serverFieldErrors(fieldErrors));
      toast.error(FIX_FIELDS_MESSAGE);
      return;
    }
    toast.error(fallback);
  };

  const isValid = async (
    formik: FormikProps<JobFormValues>,
    publishRules: boolean
  ) => {
    const errors = await validateJobForm(formik.values, publishRules);
    if (Object.keys(errors).length) {
      formik.setErrors(errors);
      toast.error(FIX_FIELDS_MESSAGE);
      return false;
    }
    return true;
  };

  const preview = async (formik: FormikProps<JobFormValues>) => {
    if (await isValid(formik, false)) {
      setPreviewForm(toDirectJobForm(formik.values));
    }
  };

  const save = async (formik: FormikProps<JobFormValues>, publish: boolean) => {
    if (!(await isValid(formik, publish || isPublished))) {
      return;
    }
    const form = toDirectJobForm(formik.values);
    setIsSaving(true);
    try {
      let saved = job
        ? await updateJob({ tenantId, jobId: job.job_id, form }).unwrap()
        : await createJob({ tenantId, form }).unwrap();
      setJob(saved);
      formik.resetForm({ values: toJobFormValues(saved.form) });
      if (!job) {
        navigate(jobPath(saved.job_id), { replace: true });
      }
      if (publish) {
        try {
          saved = await publishJob({ tenantId, jobId: saved.job_id }).unwrap();
          setJob(saved);
          toast.success("Job published");
          leave();
        } catch (error) {
          showErrors(formik, error, "Error publishing the job");
        }
        return;
      }
      if (isPublished) {
        toast.success("Job updated");
        leave();
        return;
      }
      toast.success("Draft saved");
    } catch (error) {
      showErrors(formik, error, "Error saving the job");
    } finally {
      setIsSaving(false);
    }
  };

  const unpublish = async () => {
    if (!job) {
      return;
    }
    setIsSaving(true);
    try {
      setJob(await unpublishJob({ tenantId, jobId: job.job_id }).unwrap());
      toast.success("Job unpublished");
      leave();
    } catch {
      toast.error("Error unpublishing the job");
    } finally {
      setIsSaving(false);
    }
  };

  const leave = () => navigate(ROUTES.DIRECT_JOBS, { viewTransition: true });

  const requestLeave = (formik: FormikProps<JobFormValues>) =>
    formik.dirty ? setShowLeaveModal(true) : leave();

  return (
    <Formik
      initialValues={
        initialJob ? toJobFormValues(initialJob.form) : EMPTY_JOB_FORM_VALUES
      }
      onSubmit={() => undefined}
    >
      {(formik) => (
        <>
          <PageTitle
            title={job ? "Edit job" : "Create job"}
            description="Use Preview to see the ad before you publish. Empty fields are hidden on the ad. Fields marked * are required to publish."
            breadcrumbs={[
              { label: "Jobs", onClick: () => requestLeave(formik) },
              { label: job ? job.form.title : "New job", isActive: true },
            ]}
          />
          <Grid container className="mt-4">
            <Row>
              <Grid xs={12}>
                <form noValidate onSubmit={(event) => event.preventDefault()}>
                  <JobFormFields templateOptions={templateOptions} />
                  <JobFormActions
                    job={job}
                    isDirty={formik.dirty}
                    isSaving={isSaving}
                    onCancel={() => requestLeave(formik)}
                    onPreview={() => preview(formik)}
                    onSave={(publish) => save(formik, publish)}
                    onUnpublish={() => setShowUnpublishModal(true)}
                  />
                </form>
              </Grid>
            </Row>
          </Grid>

          <JobPreviewModal
            tenantId={tenantId}
            form={previewForm}
            onClose={() => setPreviewForm(null)}
          />

          <ConfirmationModal
            show={showLeaveModal}
            onClose={() => setShowLeaveModal(false)}
            onConfirm={leave}
            title="Unsaved changes"
            confirmButtonText="Leave"
            variant={ButtonColors.DANGER}
          >
            Leave without saving? Your changes will be lost.
          </ConfirmationModal>

          <ConfirmationModal
            show={showUnpublishModal}
            onClose={() => setShowUnpublishModal(false)}
            onConfirm={async () => {
              await unpublish();
              setShowUnpublishModal(false);
            }}
            title="Unpublish job"
            confirmButtonText="Unpublish"
            variant={ButtonColors.DANGER}
          >
            Are you sure you want to unpublish this job:{" "}
            <strong>{job?.form.title}</strong>?
            {formik.dirty && " Your unsaved changes will be lost."}
          </ConfirmationModal>
        </>
      )}
    </Formik>
  );
};

const JobFormPage: React.FC = () => {
  const { jobId = NEW_JOB_ID } = useParams();
  const tenantId = useSelector(getUserTenantId);
  const [openedJobId] = useState(jobId);
  const isNew = openedJobId === NEW_JOB_ID;

  const { data: job, isError } = useGetJobQuery(
    { tenantId, jobId: openedJobId },
    { skip: isNew }
  );
  const { templates } = useGetPagesQuery(
    {},
    {
      selectFromResult: ({ data }) => ({
        templates: data?.job_templates ?? [],
      }),
    }
  );
  const { data: customerConfig } = useGetCustomerConfigQuery({ tenantId });
  const templateOptions = useMemo(
    () => jobAdTemplateOptions(templates, customerConfig?.domains ?? []),
    [templates, customerConfig]
  );

  if (!isNew && isError && !job) {
    return (
      <Alert
        className="mt-4"
        color={TalentechColors.DANGER}
        message="This job could not be loaded. It may have been deleted."
      />
    );
  }
  if (!isNew && !job) {
    return <Spinner />;
  }
  return (
    <JobForm
      tenantId={tenantId}
      initialJob={job}
      templateOptions={templateOptions}
    />
  );
};

export default JobFormPage;
